import { useParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import type { LocationType } from 'app/types/entities'
import type { IvrConfig, IvrNode, IvrNodeWire } from 'app/types/ivr'
import { notifySuccess, notifyError } from 'app/services/notify.client'
import { useLocationStore } from 'app/stores/StoreContext'
import { ObjectId } from 'lib/ObjectId'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { gqlQuery, gqlMutation } from 'app/lib/graphqlClient'
import {
  IVR_CONFIG_QUERY,
  IVR_CONFIGS_QUERY,
  IVR_PHONE_NUMBERS_QUERY,
  CREATE_IVR_CONFIG_MUTATION,
  UPDATE_IVR_CONFIG_MUTATION,
} from './graphql'

export type UseIvrBuilderReturn = {
  location: LocationType | undefined
  ivrNumbers: { id: string; phone: string; label?: string }[]
  /** True when the location has any IVR-role number, before filtering out used ones. */
  hasIvrNumbers: boolean
  selectedIvrId: string | undefined
  setSelectedIvrId: (id: string) => void
  config: IvrConfig
  configLoaded: boolean
  setConfig: (config: IvrConfig | ((prev: IvrConfig) => IvrConfig)) => void
  primaryNumberId: string | undefined
  setPrimaryNumberId: (id: string) => void
  officeTransferNumberId: string | undefined
  setOfficeTransferNumberId: (id: string) => void
  addNode: (type: IvrNode['type']) => string
  removeNode: (nodeId: string) => void
  updateNode: (nodeId: string, node: IvrNode) => void
  save: (configOverride?: IvrConfig) => Promise<boolean>
  saving: boolean
}

const emptyConfig: IvrConfig = { root: ``, nodes: {} }

/**
 * Hook for the IVR call flow builder.
 *
 * Manages the state of the IVR configuration for a location's IVR numbers:
 * - Fetches the location and extracts IVR number entries
 * - Loads IVR config from standalone IvrConfig model via GraphQL
 * - Provides CRUD operations for nodes in the call flow tree
 * - Saves config via dedicated saveIvrConfig mutation
 *
 * Used by: IvrBuilderComponent (settings/locations/$locationId/ivr)
 */
export const useIvrBuilder = (): UseIvrBuilderReturn => {
  const { locationId } = useParams()
  const store = useLocationStore()
  const [location, setLocation] = useState<LocationType | undefined>()
  const [ivrNumbersState, setIvrNumbersState] = useState<{ id: string; phone: string; label?: string }[]>([])
  const [usedPhoneNumberIds, setUsedPhoneNumberIds] = useState<Set<string>>(new Set())
  const [selectedIvrId, setSelectedIvrId] = useState<string | undefined>()
  const [config, setConfig] = useState<IvrConfig>(emptyConfig)
  const [configLoaded, setConfigLoaded] = useState<boolean>(false)
  const [configExists, setConfigExists] = useState<boolean>(false)
  const [primaryNumberId, setPrimaryNumberId] = useState<string | undefined>()
  const [officeTransferNumberId, setOfficeTransferNumberId] = useState<string | undefined>()
  const [saving, setSaving] = useState<boolean>(false)

  useEffect(() => {
    if (!locationId) return
    bg(
      store.fetchOne(locationId).then(() => {
        if (store.current) {
          setLocation(store.current)
          setPrimaryNumberId(store.current.primaryNumberId)
          setOfficeTransferNumberId(store.current.officeTransferNumberId)
        }
      }),
    )
  }, [locationId])

  // Numbers already bound to an IVR config belong to another flow, so hide them from the
  // dropdown. The currently-selected number stays visible so its own config can be edited.
  const ivrNumbers = ivrNumbersState.filter((n) => n.id === selectedIvrId || !usedPhoneNumberIds.has(n.id))

  useEffect(() => {
    if (!locationId) return
    bg(
      gqlQuery(IVR_CONFIGS_QUERY, { locationId }).then((result) => {
        const configs = result.data?.ivrConfigs ?? []
        setUsedPhoneNumberIds(new Set(configs.map((c) => c.phoneNumberId).filter((id): id is string => !!id)))
      }),
    )
  }, [locationId])

  useEffect(() => {
    if (!locationId) return
    bg(
      gqlQuery(IVR_PHONE_NUMBERS_QUERY, {
        search: `locationId:${locationId} AND roles:ivr`,
        pagination: { limit: 100 },
      }).then((result) => {
        const hits = result.data?.phoneNumbers?.hits ?? []
        setIvrNumbersState(hits.map((n) => ({ id: n.id, phone: n.phone, label: n.label ?? undefined })))
      }),
    )
  }, [locationId])

  // Load IVR config from standalone model when selected IVR number changes
  useEffect(() => {
    if (!selectedIvrId) return
    setConfigLoaded(false)
    bg(
      gqlQuery(IVR_CONFIG_QUERY, { phoneNumberId: selectedIvrId }).then((result) => {
        if (result.data?.ivrConfig) {
          const doc = result.data.ivrConfig
          // `nodes` is a JSON scalar; the wire shape is IvrNodeWire[] keyed back into a record.
          const wireNodes = doc.nodes as unknown as IvrNodeWire[]
          setConfig({
            root: doc.root ?? ``,
            enabled: doc.enabled,
            nodes: Object.fromEntries(
              wireNodes.map((n) => {
                const { id: _, ...rest } = n
                return [n.id, rest]
              }),
            ),
          })
          setConfigExists(true)
        } else {
          setConfig(emptyConfig)
          setConfigExists(false)
        }
        setConfigLoaded(true)
      }),
    )
  }, [selectedIvrId])

  useEffect(() => {
    if (ivrNumbersState.length > 0 && !selectedIvrId) {
      setSelectedIvrId(ivrNumbersState[0].id)
    }
  }, [ivrNumbersState.length])

  const addNode = useCallback((type: IvrNode['type']): string => {
    const id = ObjectId()
    const node = createDefaultNode(type)
    setConfig((prev) => {
      const updated = { ...prev, nodes: { ...prev.nodes, [id]: node } }
      if (!updated.root) updated.root = id
      return updated
    })
    return id
  }, [])

  const removeNode = useCallback((nodeId: string) => {
    setConfig((prev) => {
      const { [nodeId]: _, ...rest } = prev.nodes
      return { ...prev, nodes: rest, root: prev.root === nodeId ? Object.keys(rest)[0] || `` : prev.root }
    })
  }, [])

  const updateNode = useCallback((nodeId: string, node: IvrNode) => {
    setConfig((prev) => ({ ...prev, nodes: { ...prev.nodes, [nodeId]: node } }))
  }, [])

  const save = useCallback(
    async (configOverride?: IvrConfig): Promise<boolean> => {
      if (!location || !selectedIvrId) return false
      setSaving(true)
      try {
        const configToSave = configOverride ?? config

        // Save IVR config via create or update mutation
        const mutation = configExists ? UPDATE_IVR_CONFIG_MUTATION : CREATE_IVR_CONFIG_MUTATION
        const result = await gqlMutation(mutation, {
          input: {
            locationId: location.id!,
            phoneNumberId: selectedIvrId,
            enabled: configToSave.enabled ?? true,
            root: configToSave.root,
            nodes: Object.entries(configToSave.nodes).map(([id, node]) => ({ ...node, id })),
          },
        })

        if (result.error) {
          logError(`ivr-builder`, `save failed`, result.error)
          notifyError(`Failed to save IVR configuration`)
          return false
        }

        // After first successful create, subsequent saves use update
        if (!configExists) setConfigExists(true)

        // Save primaryNumberId/officeTransferNumberId on Location if changed
        if (
          primaryNumberId !== location.primaryNumberId ||
          officeTransferNumberId !== location.officeTransferNumberId
        ) {
          await store.update(location.id!, { primaryNumberId, officeTransferNumberId })
        }

        notifySuccess(`IVR configuration saved`)
        return true
      } finally {
        setSaving(false)
      }
    },
    [location, selectedIvrId, config, primaryNumberId, officeTransferNumberId],
  )

  return {
    location,
    ivrNumbers,
    hasIvrNumbers: ivrNumbersState.length > 0,
    selectedIvrId,
    setSelectedIvrId,
    config,
    configLoaded,
    setConfig,
    primaryNumberId,
    setPrimaryNumberId,
    officeTransferNumberId,
    setOfficeTransferNumberId,
    addNode,
    removeNode,
    updateNode,
    save,
    saving,
  }
}

/** Creates a default empty node for the given type */
const createDefaultNode = (type: IvrNode['type']): IvrNode => {
  switch (type) {
    case `greeting`:
      return { type: `greeting`, audio: { type: `tts`, text: `` }, next: `` }
    case `menu`:
      return { type: `menu`, audio: { type: `tts`, text: `` }, options: [], timeout: 10, noInput: `replay` }
    case `dial`:
      return { type: `dial`, target: `$primary`, ringCount: 5 }
    case `voicemail`:
      return { type: `voicemail`, email: `` }
    case `businessHours`:
      return { type: `businessHours`, duringHours: ``, afterHours: `` }
    case `announcement`:
      return { type: `announcement`, audio: { type: `tts`, text: `` } }
  }
}
